import React from 'react';
import cx from 'classnames';

import s from './FlowHealthBar.module.scss';

export type FlowHealthSeverity = 'error' | 'ok' | 'unknown';

/**
 * Per-flow run metrics as they arrive on the row (Phase 1 — page-scoped).
 * `errors` may be absent or a string from the backend; the index signature
 * keeps the dynamic keys the backend sends without re-introducing `any`.
 */
export interface FlowMetrics {
  errors?: number | string | null;
  records?: number;
  size?: number;
  run_id?: number;
  [key: string]: unknown;
}

/**
 * Quick-read health of a flow row, derived purely from the per-flow error count
 * already mapped onto the row (Phase 1 — page-scoped, no client-side rollup).
 *
 * - errors > 0                          → 'error'   (red)
 * - errors present and 0                → 'ok'      (green)
 * - errors absent/null (not yet known)  → 'unknown' (neutral — NOT red or green)
 *
 * An unknown error count is treated as neutral, never green: "we don't know yet"
 * must not read as "healthy".
 */
export function getFlowHealthSeverity(metrics?: FlowMetrics | null): FlowHealthSeverity {
  // Error count not yet known (no metrics, or `errors` absent/null) → neutral.
  if (metrics == null || metrics.errors == null) return 'unknown';
  return Number(metrics.errors) > 0 ? 'error' : 'ok';
}

function FlowHealthBar({ metrics }: { metrics?: FlowMetrics | null }) {
  const severity = getFlowHealthSeverity(metrics);

  return (
    <div
      role="img"
      data-testid="flow-health-bar"
      data-severity={severity}
      aria-label={`Flow health: ${severity}`}
      className={cx(s.bar, s[severity])}
    />
  );
}

export { FlowHealthBar };
